import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, setTenant, setEntity, getEntity, getTenant, getToken, apiError } from "@/lib/api";

const AtlasContext = createContext(null);
export const useAtlas = () => useContext(AtlasContext);

export function AtlasProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=logged out
  const [tenants, setTenants] = useState([]);
  const [activeTenantId, setActiveTenantId] = useState(getTenant());
  const [activeEntityId, setActiveEntityId] = useState(getEntity());
  const [ctx, setCtx] = useState(null);
  const [ctxLoading, setCtxLoading] = useState(false);

  const loadTenants = useCallback(async (u) => {
    const list = [];
    const { data } = await api.get("/me/tenants");
    list.push(...data);
    if (u?.is_platform_support) {
      try {
        const { data: st } = await api.get("/support/tenants");
        st.forEach((t) => {
          if (!list.find((x) => x.id === t.id)) {
            list.push({ id: t.id, name: t.name, slug: t.slug, logo: t.logo, support: true, active_session: !!t.active_session });
          }
        });
      } catch (e) { console.error("loadTenants: support tenants fetch failed", e); }
    }
    setTenants(list);
    return list;
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      const { data: u } = await api.get("/auth/me");
      setUser(u);
      const list = await loadTenants(u);
      let tid = getTenant();
      const stored = list.find((t) => t.id === tid);
      if (!stored || (stored.support && !stored.active_session)) tid = (list.find((t) => !t.support) || list[0])?.id || null;
      applyTenant(tid);
    } catch (e) {
      setUser(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTenants]);

  useEffect(() => {
    if (getToken()) bootstrap();
    else setUser(null);
  }, [bootstrap]);

  const loadContext = useCallback(async (tid) => {
    if (!tid) { setCtx(null); return; }
    setCtxLoading(true);
    try {
      const { data } = await api.get(`/me/context/${tid}`);
      setCtx(data);
    } catch (e) {
      setCtx(null);
    } finally {
      setCtxLoading(false);
    }
  }, []);

  const applyTenant = useCallback((tid) => {
    if (tid !== getTenant()) setEntity(null);
    setTenant(tid);
    setActiveTenantId(tid);
    setActiveEntityId(getEntity());
    loadContext(tid);
  }, [loadContext]);

  const applyEntity = useCallback((eid) => {
    setEntity(eid || null);
    setActiveEntityId(eid || null);
    window.location.reload();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    const list = await loadTenants(data.user);
    applyTenant((list.find((t) => !t.support) || list[0])?.id || null);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("logout request failed", e); }
    setToken(null);
    setTenant(null);
    setEntity(null);
    setUser(null);
    setCtx(null);
    setTenants([]);
    setActiveTenantId(null);
    window.location.assign("/login");
  };

  // Full-window navigation that moves the active tenant into the URL path. Lands on the
  // equivalent in-tenant page when it is a safe (id-free) path, otherwise that tenant's Home.
  const gotoTenant = (idOrSlug) => {
    const t = tenants.find((x) => x.id === idOrSlug || x.slug === idOrSlug);
    const slug = t?.slug || idOrSlug;
    if (!slug) return;
    try { localStorage.setItem("to_last_tenant", slug); } catch (e) { /* ignore */ }
    const cur = window.location.pathname.replace(/^\/t\/[^/]+/, "") || "/home";
    const hasId = /\/(?:[0-9a-fA-F-]{8,}|\d+)(?=\/|$)/.test(cur);
    window.location.assign(`/t/${slug}${hasId ? "/home" : (cur || "/home")}`);
  };

  const refreshTenants = () => loadTenants(user);

  const has = (perm) => !!ctx && (ctx.is_super || ctx.permissions?.includes(perm));

  const activeTenant = tenants.find((t) => t.id === activeTenantId) || ctx?.tenant;
  const companies = tenants.filter((t) => t.company);
  const mainTenant = tenants.find((t) => t.owner) || tenants.find((t) => t.member && !t.company) || tenants.find((t) => !t.company && !t.support);
  const isPortfolioAdmin = tenants.some((t) => t.company || t.owner);

  return (
    <AtlasContext.Provider
      value={{
        user, tenants, activeTenant, activeTenantId, activeEntityId, ctx, ctxLoading,
        companies, mainTenant, isPortfolioAdmin,
        login, logout, applyTenant, applyEntity, refreshTenants, loadContext, has, gotoTenant,
      }}
    >
      {children}
    </AtlasContext.Provider>
  );
}
